import { CATEGORY_SELECT_OPTIONS, VERIFICATION_CATEGORIES, mapUploadError, SelectInput } from '@app/components'
import { showMessageNotification, showMessagesNotifications, useUploadDocument } from '@app/lib'
import { type SugarVerificationCategory } from '@app/types'
import { ActionIcon, Button, Group, Stack, Text, useMantineTheme } from '@mantine/core'
import { Dropzone, type FileWithPath, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone'
import { useForm, yupResolver } from '@mantine/form'
import { IconFile, IconUpload, IconX } from '@tabler/icons-react'
import { type FC, useCallback } from 'react'
import * as yup from 'yup'

// Client-side hint only — server (Sugar `upload_maxsize`) is the authoritative cap.
const CLIENT_MAX_SIZE = 30_000_000

type FormData = {
  category: SugarVerificationCategory | ''
  file: FileWithPath | null
}

export interface VerificationDocumentUploadFormProps {
  disabled?: boolean
  enrollmentId: string
  programYearId: string
}

export const VerificationDocumentUploadForm: FC<VerificationDocumentUploadFormProps> = ({
  disabled = false,
  enrollmentId,
  programYearId
}) => {
  const theme = useMantineTheme()
  const { isUploadingDocument, uploadDocument } = useUploadDocument({ enrollmentId, programYearId })

  const form = useForm<FormData>({
    initialValues: {
      category: CATEGORY_SELECT_OPTIONS[0].value,
      file: null
    },
    validate: yupResolver(
      yup.object().shape({
        category: yup
          .string()
          .oneOf(VERIFICATION_CATEGORIES as unknown as string[], 'Please select a category')
          .required('Category is required'),
        file: yup
          .mixed()
          .required('Please select a file')
          .test('non-empty', 'File is empty', (value) => value instanceof File && value.size > 0)
      })
    ),
    validateInputOnChange: true
  })

  const handleSubmit = useCallback(async () => {
    if (!form.values.category || !form.values.file) return

    try {
      const result = await uploadDocument({
        category: form.values.category,
        file: form.values.file
      })
      showMessagesNotifications(result.messages)
      form.reset()
    } catch (error) {
      // Preserve form state on failure so the provider can adjust without re-picking the file.
      showMessageNotification(mapUploadError(error))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.category, form.values.file, uploadDocument])

  const isDisabled = disabled || isUploadingDocument

  return (
    <Stack spacing='sm'>
      <SelectInput
        data={CATEGORY_SELECT_OPTIONS}
        disabled={isDisabled}
        label='Category'
        required
        {...form.getInputProps('category')}
      />
      <Dropzone
        accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
        disabled={isDisabled || !!form.values.file}
        maxSize={CLIENT_MAX_SIZE}
        multiple={false}
        onDrop={(dropped) => form.setFieldValue('file', dropped[0] ?? null)}
        onReject={(fileRejections) => {
          const code = fileRejections[0]?.errors[0]?.code
          if (code === 'file-too-large') {
            showMessageNotification({ message: 'File exceeds the 30 MB limit.', type: 'warning' })
          } else if (code === 'file-invalid-type') {
            showMessageNotification({ message: 'Please upload a PDF or image file.', type: 'warning' })
          } else {
            showMessageNotification({ message: 'That file could not be accepted.', type: 'warning' })
          }
        }}
      >
        <Group
          position='center'
          spacing='xl'
          style={{ minHeight: 160, pointerEvents: 'none' }}
        >
          <Dropzone.Accept>
            <IconUpload
              color={theme.colors[theme.primaryColor][6]}
              size='3.2rem'
              stroke={1.5}
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX
              color={theme.colors.red[6]}
              size='3.2rem'
              stroke={1.5}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconFile
              size='3.2rem'
              stroke={1.5}
            />
          </Dropzone.Idle>
          <Text>Drop a verification document here, or click to select.</Text>
        </Group>
      </Dropzone>
      {form.values.file && (
        <Group
          noWrap
          position='apart'
        >
          <Text
            fw='bold'
            size='sm'
          >
            {form.values.file.name}
          </Text>
          <ActionIcon
            disabled={isUploadingDocument}
            onClick={() => form.setFieldValue('file', null)}
            title='Remove file'
          >
            <IconX
              color='red'
              size={18}
            />
          </ActionIcon>
        </Group>
      )}
      <Group position='right'>
        <Button
          disabled={!form.isValid() || isDisabled}
          leftIcon={<IconUpload size={18} />}
          loading={isUploadingDocument}
          onClick={handleSubmit}
        >
          Upload
        </Button>
      </Group>
    </Stack>
  )
}
